import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { fetchRuntimeConfig, otelNamespace } from "./config";
import { initBrowserOtel } from "./otel";
import { isAdmin, loadSession, type Me, type SessionState } from "./session";
import { SignInScreen } from "./SignInScreen";
import { AdminsOnlyScreen } from "./AdminsOnlyScreen";
import { Shell } from "./Shell";
import "./styles.css";

const el = document.getElementById("root");
if (!el) throw new Error("#root missing");

function Root() {
  const [session, setSession] = useState<SessionState | null>(null);

  useEffect(() => {
    void loadSession().then(setSession);
  }, []);

  if (session === null) {
    return <p className="status-message">Loading…</p>;
  }
  if (session.kind !== "signed-in") {
    return <SignInScreen state={session} />;
  }

  // The literal ask in #257: a signed-in *non*-admin gets a distinct
  // "admins only" state, not just the sign-in gate. This check is UX
  // only — real enforcement is server-side (`BffAuth.AdminPolicy` on
  // `/api/admin/*`), so nothing security-relevant depends on it.
  if (!isAdmin(session.me)) {
    return <AdminsOnlyScreen me={session.me} />;
  }

  return <Shell me={session.me} />;
}

export type { Me };

// config.json (OTel namespace) before first render, then the web tracer
// (#257, ADR 0031/0057) — spans export to the relative `/v1/traces`, which
// the BFF forwards.
void fetchRuntimeConfig().then(() => {
  initBrowserOtel(otelNamespace());
  createRoot(el).render(
    <React.StrictMode>
      <Root />
    </React.StrictMode>,
  );
});
